// Low-priority sender for training packs. In memory, at most 6 pending (the
// oldest is dropped). A pack waits staged until ITS photos have committed, then
// sends only while the photo uploader is idle and the connection is good. A
// send in flight is aborted the moment the photo queue is busy again and put
// back without using its retry. Nothing is ever shown; errors go to
// console.debug only.

import { sharedUploadQueue } from "./uploadQueue";
import { tracedFetch } from "./tracedFetch";

const API = "/api/capture";
// Room for a burst while the photo queue is busy; a pack is ~0.4 MB of memory.
const MAX_PENDING = 6;
const MAX_STAGED = 12;
const SESSION_PACKS_NO_STORAGE = 5;
const RETRY_MS = 10_000;
const POLL_MS = 5_000;
const WATCH_MS = 500;
const SEND_TIMEOUT_MS = 30_000;
// A send wedged this long is abandoned by the next kick().
const WEDGE_MS = 90_000;
const BUDGET_KEY = "cosmos.capture.trainBytes";
const PAUSE_KEY = "cosmos.capture.trainPausedDay";

const today = () => new Date().toISOString().slice(0, 10);

function defaultEnv() {
  const c = typeof navigator !== "undefined" ? navigator.connection : undefined;
  return {
    online: typeof navigator === "undefined" || navigator.onLine !== false,
    saveData: Boolean(c?.saveData),
    effectiveType: c?.effectiveType,
    visible: typeof document === "undefined" || document.visibilityState === "visible",
  };
}

function defaultStorage() {
  return window.localStorage;
}

/** Ask for a presigned PUT, then upload the pack with it. Throws; err.status is set on an HTTP refusal. */
async function defaultSend(pack, signal) {
  const res = await tracedFetch(`${API}/training-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uploadId: pack.uploadId, size: pack.size }),
    signal,
  });
  if (!res.ok) throw Object.assign(new Error(`url_${res.status}`), { status: res.status });
  const { url, headers } = await res.json();
  // The length is part of the signature: the body must be exactly `size` bytes.
  const up = await tracedFetch(url, { method: "PUT", headers, body: pack.blob, signal });
  if (!up.ok) throw Object.assign(new Error(`put_${up.status}`), { status: up.status });
}

/**
 * Everything the environment provides is injectable for the tests.
 * `dailyBytes` is the per-device day budget. The session sets it from
 * CAPTURE_TRAINING_DAILY_MB (compose.prod.yml); until then it is 0 and nothing sends.
 * @param {{dailyBytes?: number, isMainIdle?: () => boolean, env?: () => object, storage?: () => Storage, send?: (pack, signal) => Promise<void>, now?: () => number, setTimer?: Function, clearTimer?: Function}} deps
 */
export function createTrainingQueue({
  dailyBytes = 0,
  isMainIdle = () => true,
  env = defaultEnv,
  storage = defaultStorage,
  send = defaultSend,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (t) => clearTimeout(t),
  now = Date.now,
} = {}) {
  const items = [];
  // Built packs whose photos have not committed yet, by uploadId.
  const staged = new Map();
  const committedIds = [];
  let busy = null;
  let busyAt = 0;
  let gen = 0;
  let cancelInflight = null;
  let pausedDay = null; // a 429 day, when storage throws
  let again = false;
  let timer = null;
  let sessionPacks = 0;
  let shop = null;
  let inflight = null;

  function budgetUsed() {
    try {
      const raw = JSON.parse(storage().getItem(BUDGET_KEY) || "null");
      return raw && raw.day === today() ? Number(raw.bytes) || 0 : 0;
    } catch {
      return null; // storage unavailable
    }
  }

  function addBudget(bytes) {
    try {
      const used = budgetUsed() || 0;
      storage().setItem(BUDGET_KEY, JSON.stringify({ day: today(), bytes: used + bytes }));
    } catch {
      /* the per-session cap covers it */
    }
  }

  function withinBudget(size) {
    const used = budgetUsed();
    if (used === null) return sessionPacks < SESSION_PACKS_NO_STORAGE;
    return used + size <= dailyBytes;
  }

  function gatesOpen(size) {
    const e = env();
    if (!e.online || e.saveData || !e.visible) return false;
    if (e.effectiveType !== undefined && e.effectiveType !== "4g") return false;
    return isMainIdle() && withinBudget(size);
  }

  function arm(ms) {
    if (timer !== null) clearTimer(timer);
    timer = setTimer(() => {
      timer = null;
      void kick();
    }, ms);
  }

  function pausedToday() {
    if (pausedDay === today()) return true;
    try {
      return storage().getItem(PAUSE_KEY) === today();
    } catch {
      return false;
    }
  }

  function pauseToday() {
    pausedDay = today();
    try {
      storage().setItem(PAUSE_KEY, pausedDay);
    } catch {
      /* in memory only */
    }
  }

  /**
   * One send, aborted if the photo queue becomes busy meanwhile or it runs past
   * the timeout. The wait races the abort, so it never depends on fetch settling.
   */
  async function sendWatched(pack) {
    const ctl = new AbortController();
    let watch = null;
    let why = null;
    const stop = (reason) => {
      why = why || reason;
      ctl.abort();
    };
    const check = () => {
      if (!isMainIdle()) return stop("busy");
      watch = setTimer(check, WATCH_MS);
    };
    watch = setTimer(check, WATCH_MS);
    const limit = setTimer(() => stop("timeout"), SEND_TIMEOUT_MS);
    cancelInflight = () => stop("stale");
    const aborted = new Promise((_, reject) => {
      ctl.signal.addEventListener("abort", () => reject(new Error("aborted")));
    });
    aborted.catch(() => {});
    const sending = send(pack, ctl.signal);
    sending.catch(() => {});
    try {
      await Promise.race([sending, aborted]);
    } catch (err) {
      if (why === "timeout") throw new Error("timeout");
      if (ctl.signal.aborted) throw Object.assign(new Error("interrupted"), { interrupted: true });
      throw err;
    } finally {
      clearTimer(watch);
      clearTimer(limit);
    }
  }

  async function drain(mine) {
    while (items.length > 0 && mine === gen) {
      if (pausedToday()) return arm(POLL_MS); // resumes itself after UTC midnight
      const pack = items[0];
      if (pack.notBefore > now()) return arm(pack.notBefore - now());
      if (!gatesOpen(pack.size)) return arm(POLL_MS);
      inflight = pack;
      busyAt = now(); // the backstop times this send, not the whole drain
      try {
        await sendWatched(pack);
        if (mine !== gen) return undefined;
        const done = items.indexOf(pack);
        if (done >= 0) items.splice(done, 1);
        sessionPacks += 1;
        addBudget(pack.size);
      } catch (err) {
        if (mine !== gen) return undefined;
        if (err?.interrupted) {
          // Photos came first: back to waiting, retry not consumed.
          return arm(POLL_MS);
        }
        console.debug("[capture] training pack failed", err?.message);
        if (err?.status === 429) {
          // Over the server's day limit: keep the pack, stop until the next UTC day.
          pauseToday();
          return arm(POLL_MS);
        }
        const refused = err?.status >= 400 && err?.status < 500;
        if (refused || pack.attempts >= 1) {
          const at = items.indexOf(pack);
          if (at >= 0) items.splice(at, 1);
        } else {
          pack.attempts += 1;
          pack.notBefore = now() + RETRY_MS;
        }
      } finally {
        if (mine === gen) {
          inflight = null;
          cancelInflight = null;
        }
      }
    }
    return undefined;
  }

  /** Re-check the gates and send what can be sent. Safe to call at any time. */
  function kick() {
    if (busy) {
      if (now() - busyAt <= WEDGE_MS) {
        again = true;
        return busy;
      }
      abandonBusy();
    }
    const mine = ++gen;
    busyAt = now();
    busy = (async () => {
      do {
        again = false;
        await drain(mine);
      } while (again && mine === gen);
    })().finally(() => {
      if (mine === gen) busy = null;
    });
    return busy;
  }

  /** A drain stuck past WEDGE_MS: its late result is ignored, its pack counts one failure. */
  function abandonBusy() {
    gen += 1;
    busy = null;
    if (cancelInflight) cancelInflight();
    const pack = inflight;
    cancelInflight = null;
    inflight = null;
    const at = items.indexOf(pack);
    if (at < 0) return;
    if (pack.attempts >= 1) items.splice(at, 1);
    else {
      pack.attempts += 1;
      pack.notBefore = now() + RETRY_MS;
    }
  }

  function enqueue(pack) {
    items.push({ blob: pack.blob, uploadId: pack.uploadId, size: pack.size, attempts: 0, notBefore: 0 });
    while (items.length > MAX_PENDING) items.shift();
    void kick();
  }

  return {
    /** A built pack. Held until its photos commit (`committed`), dropped on `abandon`. */
    stage(pack) {
      if (!pack?.blob || !pack.uploadId) return;
      if (committedIds.includes(pack.uploadId)) return enqueue(pack);
      staged.set(pack.uploadId, pack);
      while (staged.size > MAX_STAGED) staged.delete(staged.keys().next().value);
    },
    /** The photos of this upload are committed. */
    committed(uploadId) {
      committedIds.push(uploadId);
      if (committedIds.length > MAX_STAGED * 2) committedIds.shift();
      const pack = staged.get(uploadId);
      if (pack) {
        staged.delete(uploadId);
        enqueue(pack);
      }
    },
    /** The upload was given up: its pack never sends. */
    abandon(uploadId) {
      staged.delete(uploadId);
      const at = items.findIndex((p) => p.uploadId === uploadId);
      if (at >= 0) items.splice(at, 1);
    },
    /** A different shop's session: nothing from the old one may send. */
    setShop(next) {
      if (shop && next !== shop) {
        items.length = 0;
        staged.clear();
        committedIds.length = 0;
      }
      shop = next;
    },
    /** The day budget the session was told, in bytes. */
    setBudget(bytes) {
      dailyBytes = Number(bytes) > 0 ? Number(bytes) : 0;
      void kick();
    },
    kick,
    pending: () => items.length,
    stagedCount: () => staged.size,
  };
}

let shared = null;

export function sharedTrainingQueue() {
  if (!shared) {
    const uq = sharedUploadQueue();
    shared = createTrainingQueue({ isMainIdle: () => uq.outstanding() === 0 });
    uq.listen({
      filed: (id) => shared.committed(id),
      dropped: (id) => shared.abandon(id),
    });
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => void shared.kick());
      document.addEventListener("visibilitychange", () => void shared.kick());
    }
  }
  return shared;
}
